import db from './db';
import routes_api from '../connections/routes_api';
import { getUserIdFromToken } from './jwtUtils';
import { idMapper } from './idMapper';

function currentUserId() {
  const token = localStorage.getItem('access_token');
  return getUserIdFromToken(token);
}

function nowIso() {
  return new Date().toISOString();
}

export function sameId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

export async function findRecord(store, id) {
  if (!store || id == null || id === '') return null;
  const direct = await store.get(id);
  if (direct) return direct;
  const asNumber = Number(id);
  if (Number.isFinite(asNumber) && String(asNumber) === String(id).trim()) {
    const numeric = await store.get(asNumber);
    if (numeric) return numeric;
  }
  if (typeof id !== 'string') {
    const asString = await store.get(String(id));
    if (asString) return asString;
  }
  return null;
}

function stripSyncMeta(item) {
  if (!item) return item;
  const { user_id, synced, local_updated_at, ...rest } = item;
  return rest;
}

async function pendingQueueItems() {
  return db.sync_queue
    .filter((item) => item.status === 'pending' || item.status === 'retrying')
    .toArray();
}

async function enqueue({ method, url, data }) {
  await db.sync_queue.add({
    method,
    url,
    data,
    timestamp: Date.now(),
    retries: 0,
    status: 'pending',
    conflict_status: 'none',
  });
}

export async function upsertPlannerRecord(storeName, item, { synced = true } = {}) {
  if (!item || item.id == null) return null;
  const store = db[storeName];
  const userId = currentUserId();
  const existing = await findRecord(store, item.id);
  const saved = {
    ...existing,
    ...item,
    id: existing?.id ?? item.id,
    user_id: item.user_id || existing?.user_id || userId,
    updated_at: item.updated_at || existing?.updated_at || nowIso(),
    local_updated_at: synced ? null : nowIso(),
    synced,
  };
  await store.put(saved);
  return saved;
}

async function rowsForProject(storeName, projectId) {
  const rows = await db[storeName].toArray();
  return rows.filter((row) => sameId(row.project, projectId));
}

export async function cacheProjectBoard(payload) {
  const userId = currentUserId();
  const project = payload?.project;
  if (!userId || !project?.id) return;

  await upsertPlannerRecord('planner_projects', project, { synced: true });

  const columns = payload.columns || [];
  const cards = [];

  for (const col of columns) {
    await upsertPlannerRecord(
      'planner_project_columns',
      {
        id: col.id,
        project: project.id,
        name: col.name,
        order: col.order,
        created_at: col.created_at,
        updated_at: col.updated_at,
      },
      { synced: true },
    );
    for (const card of col.cards || []) {
      cards.push(card);
      await upsertPlannerRecord('planner_project_cards', card, { synced: true });
    }
  }

  for (const card of payload.orphan_cards || []) {
    cards.push(card);
    await upsertPlannerRecord('planner_project_cards', card, { synced: true });
  }

  const columnIds = new Set(columns.map((col) => String(col.id)));
  const cardIds = new Set(cards.map((card) => String(card.id)));

  for (const col of await rowsForProject('planner_project_columns', project.id)) {
    if (col.synced && !columnIds.has(String(col.id))) {
      await db.planner_project_columns.delete(col.id);
    }
  }
  for (const card of await rowsForProject('planner_project_cards', project.id)) {
    if (card.synced && !cardIds.has(String(card.id))) {
      await db.planner_project_cards.delete(card.id);
    }
  }
}

export async function readProjectBoard(projectId) {
  const userId = currentUserId();
  if (!userId) throw new Error('User not authenticated');

  const project = await findRecord(db.planner_projects, projectId);
  if (!project || (project.user_id && String(project.user_id) !== String(userId))) {
    throw new Error('Project is not available offline');
  }

  const columns = (await rowsForProject('planner_project_columns', project.id))
    .filter((col) => !col.user_id || String(col.user_id) === String(userId))
    .sort((a, b) => a.order - b.order);
  const cards = (await rowsForProject('planner_project_cards', project.id))
    .filter((card) => !card.user_id || String(card.user_id) === String(userId));

  const columnsData = columns.map((col) => ({
    id: col.id,
    name: col.name,
    order: col.order,
    cards: cards
      .filter((card) => sameId(card.column, col.id))
      .sort((a, b) => a.order - b.order)
      .map(stripSyncMeta),
  }));

  const orphan = cards
    .filter((card) => card.column == null || card.column === '')
    .sort((a, b) => a.order - b.order)
    .map(stripSyncMeta);

  return {
    project: stripSyncMeta(project),
    columns: columnsData,
    orphan_cards: orphan,
  };
}

export async function applyColumnReorder(columns, { synced }) {
  if (!Array.isArray(columns)) return;
  for (const row of columns) {
    const existing = await findRecord(db.planner_project_columns, row.id);
    if (!existing) continue;
    await db.planner_project_columns.update(existing.id, {
      order: row.order,
      synced,
      local_updated_at: synced ? null : nowIso(),
    });
  }
}

export async function applyCardReorder(updates, { synced }) {
  if (!Array.isArray(updates)) return;
  for (const row of updates) {
    const existing = await findRecord(db.planner_project_cards, row.id);
    if (!existing) continue;
    await db.planner_project_cards.update(existing.id, {
      order: row.order,
      column: row.column_id == null || row.column_id === '' ? null : row.column_id,
      synced,
      local_updated_at: synced ? null : nowIso(),
    });
  }
}

async function requireRealId(value) {
  if (value == null || value === '') return value;
  if (String(value).startsWith('temp_')) {
    const realId = await idMapper.getRealId(value);
    if (!realId) {
      throw new Error(`Cannot sync: temporary ID ${value} not yet resolved. Dependencies must be synced first.`);
    }
    return realId;
  }
  return value;
}

export async function resolvePlannerActionPayload(data) {
  const next = { ...(data || {}) };
  if (next.project_id != null) next.project_id = await requireRealId(next.project_id);
  if (Array.isArray(data?.columns)) {
    next.columns = [];
    for (const row of data.columns) {
      next.columns.push({ ...row, id: await requireRealId(row.id) });
    }
  }
  if (Array.isArray(data?.updates)) {
    next.updates = [];
    for (const row of data.updates) {
      next.updates.push({
        ...row,
        id: await requireRealId(row.id),
        column_id:
          row.column_id == null || row.column_id === '' ? null : await requireRealId(row.column_id),
      });
    }
  }
  return next;
}

function parseBody(data) {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return data || {};
}

export async function handlePlannerProjectAction(config) {
  const url = config.url || '';
  const method = String(config.method || 'get').toLowerCase();

  if (method === 'get' && /\/projects\/[^/?]+\/board\/?/.test(url)) {
    const match = url.match(/\/projects\/([^/?]+)\/board\/?/);
    const projectId = decodeURIComponent(match[1]);
    const data = await readProjectBoard(projectId);
    return { data, status: 200, fromCache: true };
  }

  if (method === 'post' && /reorder_columns\/?/.test(url)) {
    const body = parseBody(config.data);
    await applyColumnReorder(body.columns, { synced: false });
    await enqueue({ method: 'POST', url, data: body });
    return { data: { success: true }, status: 200, fromCache: true, synced: false };
  }

  if (method === 'post' && /reorder_cards\/?/.test(url)) {
    const body = parseBody(config.data);
    await applyCardReorder(body.updates, { synced: false });
    await enqueue({ method: 'POST', url, data: body });
    return { data: { success: true }, status: 200, fromCache: true, synced: false };
  }

  throw new Error(`Planner project action not supported offline: ${url}`);
}

async function dropQueuedReferencing(projectId, extraIds = []) {
  const ids = new Set([String(projectId), ...extraIds.map((id) => String(id))]);
  const queued = await pendingQueueItems();
  for (const item of queued) {
    const data = item.data || {};
    const touches =
      ids.has(String(data.id)) ||
      sameId(data.project, projectId) ||
      sameId(data.project_id, projectId) ||
      (Array.isArray(data.columns) && data.columns.some((row) => ids.has(String(row.id)))) ||
      (Array.isArray(data.updates) && data.updates.some((row) => ids.has(String(row.id)) || ids.has(String(row.column_id))));
    if (touches) await db.sync_queue.delete(item.id);
  }
}

export async function deleteProjectCache(projectId) {
  const project = await findRecord(db.planner_projects, projectId);
  const key = project?.id ?? projectId;
  const columns = await rowsForProject('planner_project_columns', key);
  const cards = await rowsForProject('planner_project_cards', key);
  if (cards.length) await db.planner_project_cards.bulkDelete(cards.map((card) => card.id));
  if (columns.length) await db.planner_project_columns.bulkDelete(columns.map((col) => col.id));
  if (project) await db.planner_projects.delete(project.id);
}

export async function deleteProjectOffline(projectId) {
  const project = await findRecord(db.planner_projects, projectId);
  const key = project?.id ?? projectId;
  const columns = await rowsForProject('planner_project_columns', key);
  const cards = await rowsForProject('planner_project_cards', key);
  await deleteProjectCache(key);
  await dropQueuedReferencing(key, [...columns.map((col) => col.id), ...cards.map((card) => card.id)]);
  if (!String(key).startsWith('temp_')) {
    await enqueue({
      method: 'DELETE',
      url: `${routes_api.planner_projects}${key}/`,
      data: { id: key },
    });
  }
}

export async function deleteCardCache(cardId) {
  const card = await findRecord(db.planner_project_cards, cardId);
  if (card) await db.planner_project_cards.delete(card.id);
}

export async function deleteColumnCache(columnId, { purgeCards }) {
  const column = await findRecord(db.planner_project_columns, columnId);
  const key = column?.id ?? columnId;
  const cards = (await db.planner_project_cards.toArray()).filter(
    (card) => sameId(card.column, key) || sameId(card.column, columnId),
  );
  if (purgeCards) {
    if (cards.length) await db.planner_project_cards.bulkDelete(cards.map((card) => card.id));
  } else {
    const stamp = nowIso();
    for (const card of cards) {
      await db.planner_project_cards.update(card.id, {
        column: null,
        local_updated_at: stamp,
        synced: true,
      });
    }
  }
  if (column) await db.planner_project_columns.delete(column.id);
  return { key, cards };
}

export async function deleteColumnOffline(columnId, { purgeCards }) {
  const column = await findRecord(db.planner_project_columns, columnId);
  const key = column?.id ?? columnId;
  const cards = (await db.planner_project_cards.toArray()).filter(
    (card) => sameId(card.column, key) || sameId(card.column, columnId),
  );

  if (purgeCards) {
    if (cards.length) await db.planner_project_cards.bulkDelete(cards.map((card) => card.id));
  } else {
    const stamp = nowIso();
    for (const card of cards) {
      await db.planner_project_cards.update(card.id, {
        column: null,
        local_updated_at: stamp,
        synced: String(card.id).startsWith('temp_') ? false : card.synced,
      });
    }
  }
  if (column) await db.planner_project_columns.delete(column.id);

  const queued = await pendingQueueItems();
  for (const item of queued) {
    const data = item.data || {};
    if (sameId(data.id, key) && String(item.url || '').includes('project-columns')) {
      await db.sync_queue.delete(item.id);
      continue;
    }
    if (purgeCards && cards.some((card) => sameId(card.id, data.id))) {
      await db.sync_queue.delete(item.id);
      continue;
    }
    if (!purgeCards && sameId(data.column, key)) {
      await db.sync_queue.update(item.id, { data: { ...data, column: null } });
    }
    if (Array.isArray(data.columns)) {
      await db.sync_queue.update(item.id, {
        data: { ...data, columns: data.columns.filter((row) => !sameId(row.id, key)) },
      });
    }
    if (Array.isArray(data.updates)) {
      await db.sync_queue.update(item.id, {
        data: {
          ...data,
          updates: purgeCards
            ? data.updates.filter((row) => !cards.some((card) => sameId(card.id, row.id)))
            : data.updates.map((row) => (sameId(row.column_id, key) ? { ...row, column_id: null } : row)),
        },
      });
    }
  }

  if (!String(key).startsWith('temp_')) {
    const query = purgeCards ? '?delete_cards=true' : '';
    await enqueue({
      method: 'DELETE',
      url: `${routes_api.planner_project_columns}${key}/${query}`,
      data: { id: key },
    });
  }
}
